import { t, uiLanguage } from "@/lib/i18n";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { PasskeyListItem } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { KeyRound, Trash2 } from "lucide-react";
import {
  canCreatePasskey,
  isCancelled,
  registerPasskeyCeremony,
} from "@/auth/passkey";
import { useEmailConfirmed } from "@/hooks/use-email-confirmed";

export function PasskeySettings() {
  // Supabase only allows passkey registration for confirmed, non-anonymous
  // users, so hide the section entirely until the email is confirmed. The
  // hook is live (onAuthStateChange), so the section appears the moment the
  // email gets confirmed — even from another tab — without a reload.
  const emailConfirmed = useEmailConfirmed();
  const [canCreate, setCanCreate] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyListItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    canCreatePasskey().then(setCanCreate);
  }, []);

  const refresh = useCallback(
    () =>
      supabase.auth.passkey.list().then(({ data, error }) => {
        if (error) {
          setError(error.message);
          setPasskeys([]);
        } else {
          setPasskeys(data);
        }
      }),
    [],
  );

  const shown = canCreate && emailConfirmed;
  useEffect(() => {
    if (shown) refresh();
  }, [shown, refresh]);

  const handleRegister = async () => {
    setError(null);
    setBusy(true);
    try {
      await registerPasskeyCeremony();
      await refresh();
    } catch (err) {
      if (!isCancelled(err)) {
        setError(
          t("passkey-settings-couldn-t-set-up-a-passkey-on-this-device"),
        );
      }
    }
    setBusy(false);
  };

  const handleDelete = async (passkeyId: string) => {
    setError(null);
    setBusy(true);
    const { error } = await supabase.auth.passkey.delete({ passkeyId });
    if (error) {
      setError(error.message);
    } else {
      await refresh();
    }
    setBusy(false);
  };

  if (!shown) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("passkey-settings-passkeys")}</CardTitle>
        <CardDescription>
          {t(
            "passkey-settings-sign-in-with-your-fingerprint-face-or-security-key",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {passkeys === null ? (
          <p className="text-muted-foreground text-center">
            {t("common-loading")}
          </p>
        ) : passkeys.length === 0 ? (
          <p className="text-muted-foreground text-center text-sm">
            {t("passkey-settings-no-passkeys-registered-yet")}
          </p>
        ) : (
          <div className="space-y-2">
            {passkeys.map((pk) => (
              <div
                key={pk.id}
                className="flex items-center gap-3 rounded-md border p-3"
              >
                <KeyRound className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {pk.friendly_name || t("passkey-settings-passkey")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {pk.last_used_at
                      ? t("settings-passkey-dates", {
                          date: dateLabel(pk.created_at),
                          last_used: dateLabel(pk.last_used_at),
                        })
                      : t("settings-passkey-added", {
                          date: dateLabel(pk.created_at),
                        })}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(pk.id)}
                  disabled={busy}
                  title={t("passkey-settings-remove-passkey")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button onClick={handleRegister} disabled={busy} className="w-full">
          {busy
            ? t("auth-dialog-working")
            : t("passkey-settings-add-a-passkey")}
        </Button>
      </CardContent>
    </Card>
  );
}

function dateLabel(value: string): string {
  return new Date(value).toLocaleDateString(
    uiLanguage() === "en" ? undefined : uiLanguage(),
  );
}
